import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils/cn";

export type TabItemProps = {
  content: React.ReactNode;
  disable?: boolean;
  id?: string;
  label: React.ReactNode;
  onChange?: (tab: TabItemProps) => void;
  href?: string;
};

export type TabsProps = {
  tabListRef?: React.Ref<HTMLDivElement>;
  defaultTabId?: string;
  activeTabId?: string | null;
  onChange?: (tab: TabItemProps) => void;
  tabs: TabItemProps[];
};

const Tabs: React.FC<TabsProps> = ({ tabListRef, defaultTabId, activeTabId: controlledTabId, onChange, tabs }) => {
  const [internalTabId, setInternalTabId] = useState<string | undefined>(
    defaultTabId || tabs[0]?.id
  );

  const isControlled = controlledTabId !== undefined;
  const activeTabId = isControlled ? controlledTabId : internalTabId;
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabsRef = useRef<Map<string, HTMLDivElement>>(new Map());

  const handleClick = (tab: TabItemProps) => {
    if (tab.disable || !tab.id) return;
    if (!isControlled) setInternalTabId(tab.id);
    onChange?.(tab);
  };

  const updateIndicator = (tabId: string | null | undefined) => {
    if (!tabId) {
      setIndicatorStyle({ left: 0, width: 0 });
      return;
    }
    const tabElement = tabsRef.current.get(tabId);
    if (tabElement) {
      setIndicatorStyle({
        left: tabElement.offsetLeft,
        width: tabElement.offsetWidth,
      });
    }
  };

  // Update internal tab when defaultTabId changes (only in uncontrolled mode)
  useEffect(() => {
    if (defaultTabId && !isControlled) {
      setInternalTabId(defaultTabId);
    }
  }, [defaultTabId, isControlled]);

  useEffect(() => {
    updateIndicator(activeTabId);
  }, [activeTabId]);

  return (
    <div className="flex flex-col w-full">
      <div ref={tabListRef} className="flex relative border-b border-b-border">
        {tabs.map((item) => (
          <TabItem
            key={item.id}
            {...item}
            isActive={activeTabId === item.id}
            onClick={() => handleClick(item)}
            onMouseEnter={() => !item.disable && updateIndicator(item.id)}
            onMouseLeave={() => updateIndicator(activeTabId)}
            ref={(el) => {
              if (el && item.id) {
                tabsRef.current.set(item.id, el);
              }
            }}
          />
        ))}
        <div
          className="absolute -bottom-0.5 h-0.5 bg-primary transition-all duration-300 ease-out z-10"
          style={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
          }}
        />
      </div>

      {tabs
        .filter((tab) => tab.id === activeTabId && !tab.href)
        .map((tab) => (
          <div key={tab.id}>{tab.content}</div>
        ))}
    </div>
  );
};

const TabItem = React.forwardRef<
  HTMLDivElement,
  TabItemProps & {
    isActive: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
      }
      >(
      (
        { id, label, isActive, onClick, onMouseEnter, onMouseLeave, disable, href },
        ref
      ) => {
        const className = cn(
          "flex flex-col items-center justify-center px-4 py-4 cursor-pointer select-none bg-transparent transition-all font-semibold",
          isActive
            ? "text-primary hover-tint-primary"
            : "text-neutral-700 dark:text-foreground hover-tint-primary",
          disable &&
        "cursor-not-allowed text-foreground-60 hover:text-foreground-60 hover:bg-transparent"
        );

        const content = <span>{label}</span>;

        if (href && !disable) {
          return (
            <div
              id={id}
              ref={ref}
              className="inline-flex"
              role="presentation"
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              <Link
                to={href}
                className={className}
                onClick={onClick}
                aria-current={isActive ? "page" : undefined}
              >
                {content}
              </Link>
            </div>
          );
        }

        return (
          <div
            id={id}
            ref={ref}
            className={className}
            role="tab"
            aria-selected={isActive}
            tabIndex={disable ? -1 : 0}
            aria-disabled={disable}
            onClick={disable ? undefined : onClick}
            onKeyDown={(e) => { if (!disable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onClick?.(); } }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {content}
          </div>
        );
      }
      );

TabItem.displayName = "TabItem";

export default Tabs;
